<template>
  <div id="sidebar">
    <nav>
      <img src="@/assets/icons/rockestate_logo_aligned.svg" alt="" class="logo">
    </nav>
    <div class="sidebar-back">
      <img src="@/assets/icons/bttn-back.svg" class="back back-arrow" @click="goBack" />
      <img src="@/assets/icons/bttn-close-big.svg" class="close back-arrow" @click="close" />
      <div class="info-text">
        <h1>Building information</h1>
        <div class="subtitle">All extracted out of open data</div>
      </div>
    </div>
    <div class="category-info location" :key="'general'">
      <div class="info-content">
        <div class="icon"> <img src="@/assets/icons/ic-location-blue.svg" alt="location icon"> </div>
        <div class="info-name">{{house.full_address}}</div>
      </div>
    </div>
    <transition-group name="fade-up" tag="div">
    <div class="category-info" :key="'general'" v-show="animation.general">
      <div class="info-content" @click="sections.general = !sections.general">
        <div class="icon"> <img src="@/assets/icons/ic-home-blue.svg" alt="general icon"> </div>
        <div class="info-name">General house info </div>
        <i class="arrow" :class="sections.general ? 'up' : 'down'"></i>
      </div>
      <transition name="collapse-small">
        <section class="info-more" v-show="sections.general">
          <ul>
            <li>Number of addresses: <span class="info-data info-number">{{house.f_number_of_addresses | notnull}}</span>
            </li>
            <li>House type: <span class="info-data">{{ house.touching_side_type | notnull }}</span>
            </li>
            <li>Secondary buildings: <span class="info-data info-number">{{house.f_x_annexes | notnull}}</span>
            </li>
            <li>Distance to the street:
              <span>
                <span class="info-data info-number">{{house.f_distance_to_street| notnull}}</span>
                <span class="info-data info-small"> m</span>
              </span>
            </li>
            <li>Latitude: <span class="info-data info-small">{{house.f_lat| notnull}} </span>
            </li>
            <li>Longitude: <span class="info-data info-small">{{house.f_lng| notnull}}</span>
            </li>
          </ul>
        </section>
      </transition>
    </div>

    <div class="category-info" :key="'surfaces'" v-show="animation.surfaces">
      <div class="info-content" @click="sections.surfaces = !sections.surfaces">
        <div class="icon"> <img src="@/assets/icons/ic-surfaces.svg" alt="surfaces icon" class="surfaces"> </div>
        <div class="info-name">Main building dimensions </div>
        <i class="arrow" :class="sections.surfaces ? 'up' : 'down'"></i>
      </div>
      <transition name="collapse-small">
        <section class="info-more" v-show="sections.surfaces">
          <ul>
            <li>Parcel area:
              <span>
                <span class="info-data info-number">{{house.f_parcel_area| notnull}}</span>
                <span class="info-data info-small"> m<sup>2</sup>  </span>
              </span>
            </li>
            <li>Building ground surface:
              <span>
                <span class="info-data info-number">{{house.f_building_area| notnull}}</span>
                <span class="info-data info-small"> m<sup>2</sup>  </span>
              </span>
            </li>
            <li>Height:
              <span>
                <span class="info-data info-number">{{house.f_max_height| notnull}}</span>
                <span class="info-data info-small"> m </span>
              </span>
            </li>
            <li>Volume:
              <span>
                <span class="info-data info-number">{{house.f_volume| notnull}}</span>
                <span class="info-data info-small"> m<sup>3</sup> </span>
              </span>
            </li>
          </ul>
        </section>
      </transition>
    </div>

    <div class="category-info" :key="'roof'" v-show="animation.roof">
      <div class="info-content" @click="sections.roof = !sections.roof">
        <div class="icon"> <img src="@/assets/icons/ic-roof.svg" alt="roof icon"> </div>
        <div class="info-name">Roof information</div>
        <i class="arrow" :class="sections.roof ? 'up' : 'down'"></i>
      </div>
      <transition name="collapse">
        <section class="info-more" v-show="sections.roof">
          <ul>
            <li>Roof area:
              <span>
                <span class="info-data info-number">{{house.f_roof_area| notnull}}</span>
                <span class="info-data info-small"> m<sup>2</sup> </span>
              </span>
            </li>
            <li>Minimum roof height:
              <span>
                <span class="info-data info-number">{{house.f_min_height| notnull}}</span>
                <span class="info-data info-small"> m </span>
              </span>
            </li>
            <li>Maximum roof height:
              <span>
                <span class="info-data info-number">{{house.f_max_height| notnull}}</span>
                <span class="info-data info-small"> m </span>
              </span>
            </li>
            <li>Mean tilt angle:
              <span>
                <span class="info-data info-number">{{house.f_mean_tilt| notnull}}</span>
                <span class="info-data info-small"><sup class="degree">°</sup></span>
              </span>
            </li>
            <li>Flat proportion:
              <span>
                <span class="info-data info-number">{{house.f_percent_of_roof_flat *100 | notnull}}</span>
                <span class="info-data info-small">%</span>
              </span>
            </li>
          </ul>
        </section>
      </transition>
    </div>

    <div class="category-info" :key="'environment'" v-show="animation.environment">
      <div class="info-content" @click="sections.environment = !sections.environment">
        <div class="icon"> <img src="@/assets/icons/ic-environment.svg" alt="environment icon"> </div>
        <div class="info-name">Environment </div>
        <i class="arrow" :class="sections.environment ? 'up' : 'down'"></i>
      </div>
      <transition name="collapse-small">
        <section class="info-more" v-show="sections.environment">
          <ul>
            <li>Swimming pool:
              <span>
                <span class="info-data info-number">{{house.f_swimming_pools_area| notnull}}</span>
                <span class="info-data info-small"> m<sup>2</sup> </span>
              </span>
            </li>
            <li>Sea level:
              <span>
                <span class="info-data info-number">{{house.f_ground_height_above_sea| notnull}}</span>
                <span class="info-data info-small"> m</span>
              </span>
            </li>
            <li>Distance to flood zone:
              <span>
                <span class="info-data info-number">{{house.f_dist_xy_flood| notnull}}</span>
                <span class="info-data info-small"> m</span>
              </span>
            </li>
          </ul>
        </section>
      </transition>
    </div>
    </transition-group>

  </div>
</template>

<script>
export default {
  name: 'sidebar',
  props: ["house"],
  data() {
    return {
      sections: {
        general: false,
        surfaces: false,
        roof: false,
        environment: false
      },
      animation: {
        general: false,
        surfaces: false,
        roof: false,
        environment: false
      }
    }
  },
  filters: {
    notnull: function (value) {
      if(value === null || value === undefined) return '-'
      else return value;
    }
  },
  methods: {
    goBack() {
      this.$router.push({path: '/'})
    },
    close() {
      this.$emit("close");
    },
    async initAnimations() {
        await this.sleep(200)
        this.animation.general = true;
        await this.sleep(200)
        this.animation.surfaces = true;
        await this.sleep(200)
        this.animation.roof = true;
        await this.sleep(200)
        this.animation.environment = true;
    },
    sleep(time) {
      return new Promise((res, rej) => {
        setTimeout(() => { res() }, time);
      })
    }
  },
  mounted() {
    this.initAnimations()
  }
}
</script>

<style>
.surfaces {
  width: 22px;
}
</style>